/**
 * API client for stars FastAPI backend.
 * All requests go to /api/ — proxied to 127.0.0.1:8000 in dev (vite.config.ts),
 * served directly in production via nginx.
 *
 * Auth: session cookie (stars_session, HttpOnly) — browser sends automatically.
 * No manual Authorization headers needed.
 */

import type {
  ActionResponse,
  ApiError,
  Billing,
  CalcPriceResult,
  ClaimResponse,
  Client,
  ClientListItem,
  Configuration,
  ConfigurationResponse,
  Contact,
  CurrentUser,
  Dashboard,
  Deal,
  DealEvent,
  DealPatchResponse,
  DealStep,
  InvoiceResult,
  LeadListItem,
  ManualLeadResponse,
  PaginatedResponse,
  Pipeline,
  Playbook,
  PriceCatalog,
  ProposalResult,
  ProvisionClientResult,
  Reports,
  StageKey,
  TaskOut,
  TestRunResult,
} from '@/types'

const BASE = '/api'

// ---- Low-level fetch wrapper ----

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${BASE}${path}`
  const resp = await fetch(url, {
    credentials: 'include', // send session cookie
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  })

  if (!resp.ok) {
    const err: ApiError = await resp.json().catch(() => ({ error: resp.statusText }))
    // 409 gate_blocked is handled by callers — re-throw with code intact.
    // FastAPI отдаёт человеческую причину в `detail` (HTTPException), а не в
    // `error` — без этого фолбэка все объяснения сервера («автотест не пройден»,
    // «не заполнены топики») превращались в безликое «API error».
    const detail = (err as { detail?: unknown }).detail
    const message =
      err.error ??
      (typeof detail === 'string' ? detail : undefined) ??
      'API error'
    const e = new Error(message)
    ;(e as Error & { apiError: ApiError; status: number }).apiError = err
    ;(e as Error & { apiError: ApiError; status: number }).status = resp.status
    throw e
  }

  return resp.json() as Promise<T>
}

// ---- Auth ----

export const authApi = {
  me: () => request<CurrentUser>('/me'),
  login: (email: string, password: string) =>
    request<{ ok: boolean }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>('/logout', { method: 'POST' }),
}

// ---- Pipeline ----
// GET /api/pipeline is implemented (pipeline_router.py:73) — real endpoint, not a stub.

export const pipelineApi = {
  get: () => request<Pipeline>('/pipeline'),
}

// ---- Leads (inbox, stage=new_lead deals) ----

export const leadsApi = {
  list: (params?: { filter?: 'all' | 'unclaimed'; channel?: string }) => {
    const qs = new URLSearchParams()
    if (params?.filter) qs.set('filter', params.filter)
    if (params?.channel) qs.set('channel', params.channel)
    const q = qs.toString()
    return request<PaginatedResponse<LeadListItem>>(`/leads${q ? '?' + q : ''}`)
  },
  // Backend returns ClaimResponse {ok, claimed_by, claimed_at, message?}.
  // On a losing race ok=false + message — callers must surface it (do NOT assume Deal).
  claim: (dealId: string) =>
    request<ClaimResponse>(`/deals/${dealId}/claim`, { method: 'POST' }),
  // «Отказ» — сброс claimed_by. Backend endpoint: POST /api/deals/{id}/release
  // (deals_router.py монтирует POST /{deal_id}/release, НЕ /decline)
  release: (dealId: string) =>
    request<ClaimResponse>(`/deals/${dealId}/release`, { method: 'POST' }),
  // display_name ИЛИ phone обязателен (backend: ManualLeadRequest допускает любое из двух).
  // tenant_id — к какому клиенту привязать лид (передаём активного клиента полем, не строкой в note).
  createManual: (payload: {
    display_name?: string
    phone?: string
    email?: string
    business_niche?: string
    source?: string
    note?: string
    tenant_id?: string
  }) => request<ManualLeadResponse>('/leads/manual', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
}

// ---- Deals ----

export const dealsApi = {
  list: (params?: { stage?: StageKey | StageKey[] }) => {
    const qs = new URLSearchParams()
    if (params?.stage) {
      const stages = Array.isArray(params.stage) ? params.stage : [params.stage]
      stages.forEach((s) => qs.append('stage', s))
    }
    const q = qs.toString()
    return request<PaginatedResponse<Deal>>(`/deals${q ? '?' + q : ''}`)
  },
  get: (dealId: string) => request<Deal>(`/deals/${dealId}`),
  // Backend returns a TRUNCATED DealPatchResponse {id,stage_key,status,closed_at}
  // (deals_router.py DealPatchResponse), NOT the full Deal. Callers must rely on
  // query-cache invalidation to get the refreshed Deal — do not read other fields
  // off the mutation result.
  update: (dealId: string, payload: Partial<Deal>) =>
    request<DealPatchResponse>(`/deals/${dealId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /**
   * Move deal to a new stage.
   * Server enforces gate_blocked (409) and transition_not_allowed (409).
   * Returns DealPatchResponse (truncated) — see update() above.
   */
  moveStage: (dealId: string, stageKey: StageKey) =>
    request<DealPatchResponse>(`/deals/${dealId}`, {
      method: 'PATCH',
      body: JSON.stringify({ stage_key: stageKey }),
    }),

  // Steps (PLAYBOOK checklist)
  getSteps: (dealId: string) => request<DealStep[]>(`/deals/${dealId}/steps`),
  toggleStep: (dealId: string, stepKey: string, done: boolean) =>
    request<DealStep>(`/deals/${dealId}/steps/${stepKey}`, {
      method: 'PATCH',
      body: JSON.stringify({ done }),
    }),

  // Event feed (timeline)
  getEvents: (dealId: string) => request<DealEvent[]>(`/deals/${dealId}/events`),

  // Action buttons → ops_tasks queue (TZ section 6)
  // Backend returns ActionResponse {task_id,kind,status,result} (deals_router.py), NOT
  // OpsTask — task_id (not id!) is the field to poll via tasksApi.get(). task_id is
  // null when the action was fully handled inline (calc_price/gen_proposal/mark_payment).
  action: (
    dealId: string,
    payload: { label: string; kind: string; params?: Record<string, unknown> },
  ) =>
    request<ActionResponse>(`/deals/${dealId}/action`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

// ---- Contacts ----
// All three endpoints are implemented and working (contacts_router.py):
//   GET   /api/contacts/{id}   → contactsApi.get()    (contacts_router.py:178)
//   PATCH /api/contacts/{id}   → contactsApi.update() (contacts_router.py:219)
//   POST  /api/contacts/merge  → contactsApi.merge()  (contacts_router.py:123)
// Mocked in DEV mode (USE_MOCK) purely for offline dev convenience, not because
// the endpoints are missing.

export const contactsApi = {
  get: (contactId: string) => request<Contact>(`/contacts/${contactId}`),
  update: (contactId: string, payload: Partial<Contact>) =>
    request<Contact>(`/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  merge: (sourceId: string, targetId: string) =>
    request<Contact>('/contacts/merge', {
      method: 'POST',
      body: JSON.stringify({ source_id: sourceId, target_id: targetId }),
    }),
}

// ---- Ops Tasks (polling, TZ 6.4) ----
// Backend returns TaskOut (deals_router.py:1413) — a TRUNCATED OpsTask projection
// {id,kind,status,result,error,created_at,finished_at}, NOT the full ops_tasks row.

export const tasksApi = {
  get: (taskId: string) => request<TaskOut>(`/tasks/${taskId}`),
  listByDeal: (dealId: string) => request<TaskOut[]>(`/deals/${dealId}/tasks`),
}

// ---- Clients (tenants) ----
// Backend GET /api/clients returns a BARE array List[ClientListItem] (clients_router.py:229),
// NOT {items, total}.

export const clientsApi = {
  list: () => request<ClientListItem[]>('/clients'),
  get: (clientId: string) => request<Client>(`/clients/${clientId}`),
  create: (payload: { name: string; channel?: string; role?: string }) =>
    request<Client>('/clients', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

// ---- Dashboard ----

export const dashboardApi = {
  get: () => request<Dashboard>('/dashboard'),
}

// ---- Billing ----
// Backend: GET /api/billing?client_id=... (billing_router.py, прайс v3.1).
// client_id опционален на бэке (без него — нулевая заглушка), но экран биллинга
// осмыслен только для конкретного клиента — зовём с client_id.

export const billingApi = {
  get: (clientId: string) =>
    request<Billing>(`/billing?client_id=${encodeURIComponent(clientId)}`),
}

// ---- Reports ----

export const reportsApi = {
  get: (clientId?: string) => {
    const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''
    return request<Reports>(`/reports${qs}`)
  },
}

// ---- Calc price ----

export const calcPriceApi = {
  calc: (dealId: string, params?: Record<string, unknown>) =>
    request<CalcPriceResult>('/calc_price', {
      method: 'POST',
      body: JSON.stringify({ deal_id: dealId, ...(params ?? {}) }),
    }),
}

// ---- Mark payment tranche ----
// Uses PATCH /api/deals/{id} with paid_amount — returns DealPatchResponse (truncated),
// same as dealsApi.update/moveStage. Callers refresh via cache invalidation, not the result.
export const paymentApi = {
  markFirst: (dealId: string, amount: number) =>
    request<DealPatchResponse>(`/deals/${dealId}`, {
      method: 'PATCH',
      body: JSON.stringify({ paid_amount: amount, paid_at: new Date().toISOString() }),
    }),
  markSecond: (dealId: string, totalAmount: number) =>
    request<DealPatchResponse>(`/deals/${dealId}`, {
      method: 'PATCH',
      body: JSON.stringify({ paid_amount: totalAmount }),
    }),
}

// ---- Commercial Proposal (КП) ----

export const proposalApi = {
  generate: (
    dealId: string,
    params?: { channels?: string[]; upsells?: string[]; dialogs_forecast?: number },
  ) =>
    request<ProposalResult>(`/deals/${dealId}/proposal`, {
      method: 'POST',
      body: JSON.stringify(params ?? {}),
    }),
  /** Returns the URL to open in a new tab — does not fetch the blob */
  getPdfUrl: (dealId: string) => `/api/deals/${dealId}/proposal.pdf`,
}

// ---- Invoice (Счёт) ----

export const invoiceApi = {
  generate: (dealId: string, tranche: 1 | 2) =>
    request<InvoiceResult>(`/deals/${dealId}/invoice`, {
      method: 'POST',
      body: JSON.stringify({ tranche }),
    }),
  /** Returns the URL to open in a new tab */
  getPdfUrl: (dealId: string, tranche: 1 | 2) =>
    `/api/deals/${dealId}/invoice.pdf?tranche=${tranche}`,
}

// ---- Test run ----

export const testRunApi = {
  record: (
    dealId: string,
    params: { score: number; max?: number; notes?: string },
  ) =>
    request<TestRunResult>(`/deals/${dealId}/test-run`, {
      method: 'POST',
      body: JSON.stringify({ max: 30, ...params }),
    }),
}

// ---- Client provisioning ----

export const provisionApi = {
  provision: (
    dealId: string,
    params: {
      name?: string
      channel?: 'telegram' | 'max'
      bot_token?: string
      /** Токен MAX-бота клиента: без него ops отвечать в MAX от его лица не может. */
      max_bot_token?: string
      operator_chat_ids?: string[]
    },
  ) =>
    request<ProvisionClientResult>(`/deals/${dealId}/provision-client`, {
      method: 'POST',
      body: JSON.stringify(params),
    }),
}

// ---- Price catalog ----

export const catalogApi = {
  get: () => request<PriceCatalog>('/price-catalog'),
}

// ---- Deal configuration (channels, integrations, modules, forecast) ----

export const configApi = {
  get: (dealId: string) =>
    request<ConfigurationResponse>(`/deals/${dealId}/configuration`),
  put: (dealId: string, body: Omit<Configuration, never>) =>
    request<ConfigurationResponse>(`/deals/${dealId}/configuration`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
}

// ---- Stage playbook ----

export const playbookApi = {
  get: () => request<Playbook>('/playbook'),
}

// ---- База знаний (БЗ) ----
// GET  /api/deals/{id}/kb          — топики с текстом (чтение перед одобрением).
// POST /api/deals/{id}/kb/approve  — одобряет все топики → даёт gate_2.

export interface KbTopicItem {
  id: string
  topic: string
  title: string | null
  current_version_id: string | null
  current_status: string | null
  version: number | null
  content_md: string | null
  authored_by_operator: boolean
  updated_at: string | null
}

export const kbApi = {
  list: (dealId: string) => request<KbTopicItem[]>(`/deals/${dealId}/kb`),
  /**
   * Сохранить топик: создаётся НОВАЯ версия с автором-оператором, и она СРАЗУ
   * уходит боту (09.08). Промежуточного «черновика», который надо потом
   * одобрять, больше нет — сохранил значит работает. Пересборка БЗ (kb_build)
   * такие версии не перезаписывает.
   *
   * `published` в ответе говорит, дошла ли правка до бота; `publish_note` —
   * почему не дошла (кабинет клиента ещё не создан, Тоня недоступна).
   */
  save: (dealId: string, topic: string, contentMd: string, title?: string) =>
    request<{ published?: boolean; publish_note?: string | null }>(
      `/deals/${dealId}/kb/${topic}`, {
        method: 'PUT',
        body: JSON.stringify({ content_md: contentMd, ...(title ? { title } : {}) }),
      }),
  /**
   * Чей сценарий разговора главнее (10.08). `own` — ведёт инструкция клиента,
   * наш встроенный порядок шагов ей уступает.
   */
  scenarioMode: (dealId: string) =>
    request<{ own_scenario: boolean; available: boolean }>(
      `/deals/${dealId}/agent/scenario-mode`),
  setScenarioMode: (dealId: string, own: boolean) =>
    request<{ own_scenario: boolean }>(`/deals/${dealId}/agent/scenario-mode`, {
      method: 'PUT', body: JSON.stringify({ own_scenario: own }),
    }),
  files: (dealId: string) =>
    request<{ filename: string; size_bytes: number; uploaded_at: number; kind: 'table' | 'document' }[]>(
      `/deals/${dealId}/kb/files`,
    ),
  // Google-документы и таблицы, подключённые по ссылке.
  googleList: (dealId: string) =>
    request<{ id: string; kind: 'gdoc' | 'gsheet'; url: string; title: string
              topic: string; last_synced_at: string | null; last_error: string | null }[]>(
      `/deals/${dealId}/kb/google`,
    ),
  googleConnect: (dealId: string, url: string) =>
    request<{ id: string; title: string; topic: string; extracted_chars: number }>(
      `/deals/${dealId}/kb/google`,
      { method: 'POST', body: JSON.stringify({ url }) },
    ),
  googleSync: (dealId: string, id: string) =>
    request<{ ok: boolean; title: string; extracted_chars: number }>(
      `/deals/${dealId}/kb/google/${id}/sync`, { method: 'POST' },
    ),
  googleDisconnect: (dealId: string, id: string) =>
    request<{ ok: boolean; title: string }>(`/deals/${dealId}/kb/google/${id}`, {
      method: 'DELETE',
    }),
  // Прикрепить файл к базе знаний БЕЗ выбора раздела — раздел выведет сервер
  // из имени файла (см. attach_kb_file).
  attachFile: async (dealId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const resp = await fetch(`/api/deals/${dealId}/kb/files`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(
        (typeof err.detail === 'string' ? err.detail : undefined) ??
          'Не удалось прикрепить файл.',
      )
    }
    return resp.json() as Promise<{
      filename: string; topic: string; title: string
      extracted_chars: number; truncated: boolean; note: string | null
    }>
  },
  removeFile: (dealId: string, filename: string) =>
    request<{ ok: boolean }>(`/deals/${dealId}/kb/files/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    }),
  // Создать раздел с произвольным названием — технический ключ выводит сервер.
  createTopic: (dealId: string, title: string) =>
    request<{ topic: string; title: string }>(`/deals/${dealId}/kb/topics`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    }),
  rename: (dealId: string, topic: string, title: string) =>
    request<{ topic: string; title: string }>(`/deals/${dealId}/kb/${topic}/title`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  remove: (dealId: string, topic: string) =>
    request<{ ok: boolean; topic: string }>(`/deals/${dealId}/kb/${topic}`, {
      method: 'DELETE',
    }),
  approve: (dealId: string) =>
    request<{ gate_key: string; result: string }>(`/deals/${dealId}/kb/approve`, {
      method: 'POST',
    }),
  /**
   * Прикрепить файл к топику: сервер разбирает xlsx/docx/pdf/csv/txt в текст и
   * вставляет его в топик разделом «Из файла: <имя>» (новая версия-черновик).
   * Файл-первоисточник остаётся в папке клиента. FormData шлём БЕЗ Content-Type —
   * его с boundary проставляет сам браузер.
   */
  uploadFile: async (dealId: string, topic: string, file: File): Promise<KbFileUploadResult> => {
    const body = new FormData()
    body.append('file', file)
    const resp = await fetch(`/api/deals/${dealId}/kb/${topic}/file`, {
      method: 'POST',
      credentials: 'include',
      body,
    })
    if (!resp.ok) {
      let detail = 'Не удалось загрузить файл.'
      try {
        const data = await resp.json()
        if (typeof data?.detail === 'string') detail = data.detail
      } catch {
        /* тело не JSON — оставляем общий текст */
      }
      throw new Error(detail)
    }
    return resp.json()
  },
}

export interface KbFileUploadResult {
  ok: boolean
  topic: string
  filename: string
  size_bytes: number
  extracted_chars: number
  truncated: boolean
  note: string | null
  version: number
  content_md: string
}

// ---- Автотест каналов ----
// GET /api/deals/{id}/channels/test — результат последнего прогона.

export interface ChannelTestItem {
  channel: string
  ok: boolean
  detail: string
  mode: 'live' | 'offline-check' | 'structural' | string
  warnings?: string[]
  webhook?: {
    url?: string
    pending_update_count?: number | null
    last_error_message?: string | null
  } | null
}

export interface ChannelTestReport {
  ran: boolean
  all_ok: boolean | null
  channels: ChannelTestItem[]
  finished_at: string | null
  task_id?: string
}

export interface ChannelCatalogItem {
  key: string
  name: string
  kind: 'channel' | 'integration'
  group: string
  desc: string
  available: boolean
  connected: boolean
  detail: string
}

export const testChatApi = {
  // Один вопрос боту клиента в изоляции (живые диалоги не трогаются).
  ask: (
    dealId: string,
    message: string,
    history: { role: 'user' | 'assistant'; content: string }[],
  ) =>
    request<{ answer: string; handoff: boolean }>(
      `/deals/${dealId}/test-chat`,
      { method: 'POST', body: JSON.stringify({ message, history }) },
    ),
}

export const replyDelayApi = {
  get: (dealId: string) =>
    request<{ reply_delay_sec: number | null; available: boolean }>(
      `/deals/${dealId}/reply-delay`,
    ),
  set: (dealId: string, sec: number | null) =>
    request<{ reply_delay_sec: number | null }>(`/deals/${dealId}/reply-delay`, {
      method: 'PUT',
      body: JSON.stringify({ reply_delay_sec: sec }),
    }),
}

export const selfAgentApi = {
  get: () =>
    request<{ tenant_id: string; system_prompt: string
              effective_system_prompt: string
              kb_topics: { topic: string; title?: string; content_md?: string }[] }>(
      '/self-agent',
    ),
  save: (payload: { system_prompt?: string; kb_topics?: unknown[] }) =>
    request<{ ok: boolean }>('/self-agent', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  files: () => request<{ filename: string; size_bytes: number; uploaded_at: number
                        kind: 'table' | 'document' }[]>('/self-agent/files'),
  attachFile: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await fetch('/api/self-agent/files', { method: 'POST', credentials: 'include', body: fd })
    if (!r.ok) {
      const e = await r.json().catch(() => ({}))
      throw new Error(typeof e.detail === 'string' ? e.detail : 'Не удалось прикрепить файл.')
    }
    return r.json() as Promise<{ filename: string; topic: string; title: string
                                 extracted_chars: number; truncated: boolean; note: string | null }>
  },
  removeFile: (filename: string) =>
    request<{ ok: boolean }>(`/self-agent/files/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
  googleList: () =>
    request<{ id: string; kind: 'gdoc' | 'gsheet'; url: string; title: string
              topic: string; last_synced_at: string | null; last_error: string | null }[]>(
      '/self-agent/google'),
  googleConnect: (url: string) =>
    request<{ id: string; title: string; extracted_chars: number }>('/self-agent/google', {
      method: 'POST', body: JSON.stringify({ url }),
    }),
  googleSync: (id: string) =>
    request<{ ok: boolean; title: string; extracted_chars: number }>(
      `/self-agent/google/${id}/sync`, { method: 'POST' }),
  googleDisconnect: (id: string) =>
    request<{ ok: boolean }>(`/self-agent/google/${id}`, { method: 'DELETE' }),
  versions: () =>
    request<{ id: string; author: string | null; created_at: string
              prompt_len: number; topics: number; preview: string }[]>('/self-agent/versions'),
  restore: (id: string) =>
    request<{ ok: boolean }>(`/self-agent/versions/${id}/restore`, { method: 'POST' }),
  getDelay: () => request<{ reply_delay_sec: number | null }>('/self-agent/reply-delay'),
  setDelay: (sec: number | null) =>
    request<{ reply_delay_sec: number | null }>('/self-agent/reply-delay', {
      method: 'PUT', body: JSON.stringify({ reply_delay_sec: sec }),
    }),
  channels: () =>
    request<{ key: string; name: string; connected: boolean; detail: string
              kind?: string }[]>('/self-agent/channels'),
  ask: (message: string, history: { role: 'user' | 'assistant'; content: string }[]) =>
    request<{ answer: string; handoff: boolean }>('/self-agent/test-chat', {
      method: 'POST',
      body: JSON.stringify({ message, history }),
    }),
}

/**
 * Специалисты внутри агента (08.08) и редактор любого агента по его slug.
 *
 * Специалист устроен так же, как основной агент: своя инструкция, своя база
 * знаний, свои файлы и своя история. Разница только в адресе — поэтому один
 * набор ручек обслуживает и нашу Тоню, и агентов клиентов.
 */
export const agentsApi = {
  list: (parent: string) =>
    request<{ slug: string; key: string; name: string; routing_hint: string
              prompt_len: number; topics: number }[]>(
      `/agents/${encodeURIComponent(parent)}/subagents`),
  create: (parent: string, name: string, routing_hint: string) =>
    request<{ slug: string; key: string; name: string }>(
      `/agents/${encodeURIComponent(parent)}/subagents`, {
        method: 'POST', body: JSON.stringify({ name, routing_hint }),
      }),
  updateMeta: (slug: string, payload: { name?: string; routing_hint?: string }) =>
    request<{ ok: boolean }>(`/agents/${encodeURIComponent(slug)}/meta`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
  remove: (slug: string) =>
    request<{ ok: boolean }>(`/agents/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  get: (slug: string) =>
    request<{ tenant_id: string; system_prompt: string; effective_system_prompt: string
              kb_topics: { topic: string; title?: string; content_md?: string }[]
              parent_slug: string | null; name: string; routing_hint: string }>(
      `/agents/${encodeURIComponent(slug)}`),
  save: (slug: string, payload: { system_prompt?: string; kb_topics?: unknown[] }) =>
    request<{ ok: boolean }>(`/agents/${encodeURIComponent(slug)}`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
  ask: (slug: string, message: string) =>
    request<{ answer: string; no_data: boolean }>(
      `/agents/${encodeURIComponent(slug)}/test`, {
        method: 'POST', body: JSON.stringify({ message }),
      }),
  versions: (slug: string) =>
    request<{ id: string; author: string | null; created_at: string
              prompt_len: number; topics: number; preview: string }[]>(
      `/agents/${encodeURIComponent(slug)}/versions`),
  restore: (slug: string, id: string) =>
    request<{ ok: boolean }>(`/agents/${encodeURIComponent(slug)}/versions/${id}/restore`,
      { method: 'POST' }),

  files: (slug: string) =>
    request<{ filename: string; size_bytes: number; uploaded_at: number
              kind: 'table' | 'document' }[]>(`/agents/${encodeURIComponent(slug)}/files`),
  attachFile: async (slug: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await fetch(`/api/agents/${encodeURIComponent(slug)}/files`,
      { method: 'POST', credentials: 'include', body: fd })
    if (!r.ok) {
      const e = await r.json().catch(() => ({}))
      throw new Error(typeof e.detail === 'string' ? e.detail : 'Не удалось прикрепить файл.')
    }
    return r.json() as Promise<{ filename: string; topic: string; title: string
                                 extracted_chars: number; truncated: boolean; note: string | null }>
  },
  removeFile: (slug: string, filename: string) =>
    request<{ ok: boolean }>(
      `/agents/${encodeURIComponent(slug)}/files/${encodeURIComponent(filename)}`,
      { method: 'DELETE' }),
  googleList: (slug: string) =>
    request<{ id: string; kind: 'gdoc' | 'gsheet'; url: string; title: string
              topic: string; last_synced_at: string | null; last_error: string | null }[]>(
      `/agents/${encodeURIComponent(slug)}/google`),
  googleConnect: (slug: string, url: string) =>
    request<{ id: string; title: string; extracted_chars: number }>(
      `/agents/${encodeURIComponent(slug)}/google`, {
        method: 'POST', body: JSON.stringify({ url }),
      }),
  googleSync: (slug: string, id: string) =>
    request<{ ok: boolean; title: string; extracted_chars: number }>(
      `/agents/${encodeURIComponent(slug)}/google/${id}/sync`, { method: 'POST' }),
  googleDisconnect: (slug: string, id: string) =>
    request<{ ok: boolean }>(`/agents/${encodeURIComponent(slug)}/google/${id}`,
      { method: 'DELETE' }),

  // Переписки агента: список и одна целиком.
  dialogs: (slug: string, params: { channel?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.channel) q.set('channel', params.channel)
    if (params.limit) q.set('limit', String(params.limit))
    if (params.offset) q.set('offset', String(params.offset))
    const tail = q.toString() ? `?${q}` : ''
    return request<{ total: number; items: {
      id: string; channel: string; user_id: string; username: string | null
      full_name: string | null; status: string; qualification: string | null
      messages: number; last_text: string; last_role: string; handed_off: boolean
      created_at: string | null; updated_at: string | null }[] }>(
      `/agents/${encodeURIComponent(slug)}/dialogs${tail}`)
  },
  dialogMessages: (slug: string, id: string) =>
    request<{ dialog: { channel: string; user_id: string; username: string | null
                        full_name: string | null; status: string; created_at: string | null }
              messages: { role: string; content: string; created_at: string | null
                          model: string | null }[] }>(
      `/agents/${encodeURIComponent(slug)}/dialogs/${encodeURIComponent(id)}`),

  // Заявки покупателей клиента — то, что бот довёл до передачи человеку.
  requests: (slug: string, status = '') =>
    request<{ id: string; status: string; summary: string; channel: string
              amount: number | null; dialog_id: string | null; contact_name: string
              contact_phone: string; assigned_to: string; created_at: string | null }[]>(
      `/agents/${encodeURIComponent(slug)}/requests${status ? `?status=${status}` : ''}`),
  setRequestStatus: (slug: string, id: string, status: string) =>
    request<{ ok: boolean; status: string }>(
      `/agents/${encodeURIComponent(slug)}/requests/${id}`, {
        method: 'PUT', body: JSON.stringify({ status }),
      }),

  // Виджет на сайте клиента. Ключ приходит только на чтение: сниппет уже стоит
  // на сайте, и смена ключа погасила бы там чат.
  widget: (slug: string) =>
    request<{ exists: boolean; widget_key: string; snippet: string
              allowed_origins: string[]; title: string; greeting: string
              accent: string; avatar: string; quick_replies: string[]
              active: boolean }>(
      `/agents/${encodeURIComponent(slug)}/widget`),
  saveWidget: (slug: string, payload: {
    allowed_origins?: string[]; title?: string; greeting?: string
    accent?: string; avatar?: string; quick_replies?: string[]
  }) =>
    request<{ widget_key: string; snippet: string }>(
      `/agents/${encodeURIComponent(slug)}/widget`, {
        method: 'PUT', body: JSON.stringify(payload),
      }),
}

export const channelsApi = {
  lastTest: (dealId: string) =>
    request<ChannelTestReport>(`/deals/${dealId}/channels/test`),
  // Каталог каналов со статусом подключения (вкладка «Каналы»).
  catalog: (dealId: string) =>
    request<{ tenant_id: string | null; channels: ChannelCatalogItem[] }>(
      `/deals/${dealId}/channels`,
    ),
}

// ---- Manual gate confirm (Ворота 1 — созвон назначен) ----

export const gateApi = {
  mark: (dealId: string, gateKey: string, passed: boolean) =>
    request<{ gate_key: string; result: string }>(`/deals/${dealId}/gate`, {
      method: 'POST',
      body: JSON.stringify({ gate_key: gateKey, passed }),
    }),
}
